/**
 * The card networks a seller's checkout may list (LIS-1173, the purchase
 * design's §2.8), and what the card vault's brand string is among them. Its
 * own subpath, shared by the identity card wire (buyer-identity-client) and
 * the purchase's payment policy (platform-wire), which depend on no package
 * of each other's.
 */

/** The networks sellers list, in the purchase's terms. */
export const cardBrands = [
  "visa",
  "mastercard",
  "amex",
  "maestro",
  "discover",
  "diners",
  "jcb",
  "unionpay",
  "elo",
  "hipercard",
] as const;
export type CardBrand = (typeof cardBrands)[number];

/**
 * A saved card's network: one of `cardBrands`; `other`, a classified
 * network outside them (Carte Bancaire, RuPay, Mir), which a seller that
 * lists its brands refuses; or `unclassified`, where the vault gave no
 * brand ("", "Card", "unknown"), which is taken, as it always was, the
 * seller's own checkout deciding.
 */
export const cardBrandIds = [...cardBrands, "other", "unclassified"] as const;
export type CardBrandId = (typeof cardBrandIds)[number];

/** A brand as compared: lower case, letters and digits only ("Master
 *  Card" is "mastercard"). */
const brandKey = (brand: string) => brand.toLowerCase().replace(/[^a-z0-9]/g, "");

/** What the vault gives a card it could not classify. */
const UNCLASSIFIED = new Set(["", "card", "unknown"]);

/** Each network by the keys its names start with: a kind of one is that
 *  network ("Visa Electron" is a Visa), as a seller's list matched a brand
 *  by its prefix. */
const PREFIXES: readonly (readonly [CardBrand, readonly string[]])[] = [
  ["visa", ["visa"]],
  ["mastercard", ["mastercard"]],
  ["amex", ["americanexpress", "amex"]],
  ["maestro", ["maestro"]],
  ["discover", ["discover"]],
  ["diners", ["dinersclub", "diners"]],
  ["jcb", ["jcb"]],
  ["unionpay", ["unionpay", "chinaunionpay"]],
  ["elo", ["elo"]],
  ["hipercard", ["hipercard"]],
];

/** The card's network, from the brand string the vault stored with it. */
export function cardBrandIdOf(brand: string | null | undefined): CardBrandId {
  const key = brandKey(brand ?? "");
  if (UNCLASSIFIED.has(key)) return "unclassified";
  for (const [id, prefixes] of PREFIXES) if (prefixes.some((prefix) => key.startsWith(prefix))) return id;
  return "other";
}

/** Whether a seller whose checkout takes `brands` (null: any card) takes a
 *  card of this network: a listed one, or an unclassified one. */
export const cardBrandTaken = (brands: readonly CardBrand[] | null, card: CardBrandId): boolean =>
  brands === null || card === "unclassified" || (brands as readonly string[]).includes(card);
